import { useMsal } from "@azure/msal-react";
import { QueryClientContext } from "@tanstack/react-query";
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { getUser, listAllOrganizations, Organization, UserProject, UserWithProjects } from "../api/api";
import { API_SCOPE } from "../config/authConfig";

interface AccountInfo {
  name?: string;
  username?: string;
  [key: string]: unknown;
}

interface UserOrganizationInfo {
  organizationId: string;
  organizationName: string;
  role?: string;
}

interface AuthContextProps {
  account: AccountInfo | null;
  isLoggedIn: boolean;
  isGuest: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  login: () => void;
  logout: () => void;
  continueAsGuest: () => void;
  accessToken: string | null;
  getAccessToken: () => Promise<string | null>;
  storageAccessToken: string | null;
  getStorageAccessToken: () => Promise<string | null>;
  // Enhanced user data
  userInfo: UserWithProjects | null;
  userProjects: UserProject[];
  userOrganizations: UserOrganizationInfo[];
  allOrganizations: Organization[];
  primaryOrganizationId: string | null;
  refreshUserData: () => Promise<void>;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { instance, accounts } = useMsal();
  const queryClient = useContext(QueryClientContext);
  const activeAccount = accounts[0] ?? null;
  const activeUserId = (activeAccount?.idTokenClaims as { oid?: string } | undefined)?.oid ?? null;
  const activeUserIdRef = useRef(activeUserId);
  activeUserIdRef.current = activeUserId;
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isGuest, setIsGuest] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [account, setAccount] = useState<AccountInfo | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [storageAccessToken, setStorageAccessToken] = useState<string | null>(null);

  // Enhanced user data
  const [userInfo, setUserInfo] = useState<UserWithProjects | null>(null);
  const [userProjects, setUserProjects] = useState<UserProject[]>([]);
  const [userOrganizations, setUserOrganizations] = useState<UserOrganizationInfo[]>([]);
  const [allOrganizations, setAllOrganizations] = useState<Organization[]>([]);
  const [primaryOrganizationId, setPrimaryOrganizationId] = useState<string | null>(null);

  const hasCurrentUser = isLoggedIn && !isGuest && !!activeUserId && userInfo?.id === activeUserId;

  useEffect(() => {
    const filters = {
      predicate: (query: { queryKey: readonly unknown[] }) =>
        ["personalAccessUsage", "adminProjectCosts"].includes(String(query.queryKey[0])) && (!isLoggedIn || query.queryKey[1] !== activeUserId),
    };
    void queryClient?.cancelQueries(filters);
    queryClient?.removeQueries(filters);
  }, [activeUserId, isLoggedIn, queryClient]);

  // Define token functions first (before they're used in callbacks) - memoized to prevent recreation
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!accounts.length) return null;

    try {
      // Define the scopes you need - adjust based on your API requirements
      const scopes = [API_SCOPE]; // Add any other scopes your API requires

      const response = await instance.acquireTokenSilent({
        scopes,
        account: accounts[0],
      });

      setAccessToken(response.accessToken);
      return response.accessToken;
    } catch (error) {
      console.error("Failed to acquire token silently:", error);

      // Token might be expired, try interactive method as fallback
      try {
        const response = await instance.acquireTokenPopup({
          scopes: ["User.Read", API_SCOPE],
        });

        setAccessToken(response.accessToken);
        return response.accessToken;
      } catch (interactiveError) {
        console.error("Failed to acquire token interactively:", interactiveError);
        return null;
      }
    }
  }, [accounts, instance]);

  const getStorageAccessToken = useCallback(async (): Promise<string | null> => {
    if (!accounts.length) return null;

    try {
      // Define the scopes you need - adjust based on your API requirements
      const scopes = ["https://storage.azure.com/user_impersonation"]; // Add any other scopes your API requires

      const response = await instance.acquireTokenSilent({
        scopes,
        account: accounts[0],
      });

      setStorageAccessToken(response.accessToken);
      return response.accessToken;
    } catch (error) {
      console.error("Failed to acquire storage token silently:", error);
      // Token might be expired, try interactive method as fallback
      try {
        const response = await instance.acquireTokenPopup({
          scopes: ["User.Read", "https://storage.azure.com/user_impersonation"],
        });

        setStorageAccessToken(response.accessToken);
        return response.accessToken;
      } catch (interactiveError) {
        console.error("Failed to acquire token interactively:", interactiveError);
        return null;
      }
    }
  }, [accounts, instance]);

  // Refresh user data function (now defined after getAccessToken)
  const refreshUserData = useCallback(async () => {
    const claims = accounts[0]?.idTokenClaims as { oid?: string } | undefined;
    const userOid = claims?.oid;
    if (!userOid) return;
    const token = await getAccessToken();
    if (!token || activeUserIdRef.current !== userOid) return;

    try {
      const user: UserWithProjects = await getUser(token, userOid);
      if (activeUserIdRef.current !== userOid || user.id !== userOid) return;
      setUserInfo(user);
      setIsAdmin(user.isOwner ?? false);
      setUserProjects(user.projects || []);

      // Handle user organizations
      if (user.organizations && user.organizations.length > 0) {
        setUserOrganizations(user.organizations);
        setPrimaryOrganizationId(user.organizations[0].organizationId);
      } else if (user.organizationId) {
        setUserOrganizations([{ organizationId: user.organizationId, organizationName: "" }]);
        setPrimaryOrganizationId(user.organizationId);
      }

      // Fetch all organizations (for admin users, returns all; for others, gracefully returns [])
      const orgs = await listAllOrganizations(token);
      if (activeUserIdRef.current !== userOid) return;
      setAllOrganizations(orgs);

      // Update organization names in userOrganizations
      if (user.organizations && orgs.length > 0) {
        const enrichedUserOrgs = user.organizations.map((userOrg) => {
          const org = orgs.find((o) => o.id === userOrg.organizationId);
          return {
            ...userOrg,
            organizationName: org?.name || userOrg.organizationName,
          };
        });
        setUserOrganizations(enrichedUserOrgs);
      }
    } catch (error) {
      console.error("Failed to fetch user data:", error);
    }
  }, [accounts, getAccessToken]);

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      setIsLoading(true);
      setIsAdmin(false);
      setUserInfo(null);
      setUserProjects([]);
      setUserOrganizations([]);
      setAllOrganizations([]);
      setPrimaryOrganizationId(null);
      if (accounts.length > 0) {
        setIsLoggedIn(true);
        setIsGuest(false);
        setAccount(accounts[0]);
        await refreshUserData();
      } else {
        setIsLoggedIn(false);
        setAccount(null);
        setAccessToken(null);
        setIsAdmin(false);
        setUserInfo(null);
        setUserProjects([]);
        setUserOrganizations([]);
        setAllOrganizations([]);
        setPrimaryOrganizationId(null);
      }
      if (!cancelled) setIsLoading(false);
    };

    init();
    return () => {
      cancelled = true;
    };
  }, [accounts, refreshUserData]);

  const login = () => {
    instance
      .loginPopup({
        scopes: ["User.Read", API_SCOPE],
        extraScopesToConsent: ["https://storage.azure.com/user_impersonation"],
      })
      .then((response) => {
        setIsLoggedIn(true);
        setAccount(response.account);
        setAccessToken(response.accessToken);
        instance
          .acquireTokenSilent({
            scopes: [API_SCOPE],
            account: response.account,
          })
          .then((tokenResponse) => {
            setStorageAccessToken(tokenResponse.accessToken);
          })
          .catch((tokenError) => {
            console.error("Silent token acquisition failed after login:", tokenError);
          });
      })
      .catch((error) => {
        console.error("Popup login failed:", error);
      });
  };

  const continueAsGuest = () => {
    setIsGuest(true);
  };

  const logout = () => {
    instance
      .logoutPopup()
      .then(() => {
        setIsLoggedIn(false);
        setIsGuest(false);
        setAccount(null);
        setAccessToken(null);
        setStorageAccessToken(null);
        setUserInfo(null);
        setIsAdmin(false);
        setUserProjects([]);
        setUserOrganizations([]);
        setAllOrganizations([]);
        setPrimaryOrganizationId(null);
      })
      .catch((error) => {
        console.error("Logout failed:", error);
      });
  };

  return (
    <AuthContext.Provider
      value={{
        account: activeAccount ?? account,
        isLoggedIn,
        isGuest,
        isAdmin: hasCurrentUser && isAdmin,
        isLoading,
        login,
        logout,
        continueAsGuest,
        accessToken,
        getAccessToken,
        storageAccessToken,
        getStorageAccessToken,
        userInfo: hasCurrentUser ? userInfo : null,
        userProjects: hasCurrentUser ? userProjects : [],
        userOrganizations: hasCurrentUser ? userOrganizations : [],
        allOrganizations: hasCurrentUser ? allOrganizations : [],
        primaryOrganizationId: hasCurrentUser ? primaryOrganizationId : null,
        refreshUserData,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext };
